/**
 * Position ROE calculation — single source of truth.
 *
 * Returns the ROI of a PnL value as a fraction (0.47 == 47%). The basis is the
 * margin committed to the position: prefer the API's `margin_used`
 * (= position_value / leverage) and otherwise reconstruct it from the user's
 * selected leverage via the standard ROE-on-initial-margin identity:
 *
 *   ROE = pnl / (entry_value / leverage)
 *       = (pnl / entry_value) × leverage
 *       = raw_return × leverage
 *
 * Identity above holds for any leverage from 1x to 100x. Behaviour at the
 * edges is exhaustively documented in the test cases at the bottom of this
 * file and verified by ./positionRoi.test.ts.
 *
 * The function is intentionally tolerant: any missing / non-numeric input is
 * treated as 0 and routed to the next sensible fallback rather than throwing.
 */

import { MarginTypeResponse } from "../codegen-api";
import { normalizeMarginType } from "./margin";
import { apiNum } from "./math";

/** Minimum subset of a position needed to compute ROE. */
export interface IRoiInputs {
  amount?: string | number | null;
  avg_entry_price?: string | number | null;
  leverage?: string | number | null;
  margin_type?: MarginTypeResponse | string | null;
  isolated_margin?: string | number | null;
  initial_margin?: string | number | null;
  maintenance_margin?: string | number | null;
  /** API-provided current margin (= position_value / leverage). Preferred basis. */
  margin_used?: string | number | null;
  /** API-provided ROE fraction (= unrealized_pnl / margin_used). */
  return_on_equity?: string | number | null;
}

const num = (v: unknown): number => {
  if (v == null) return 0;
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * Pick the margin denominator for ROE.
 *
 * Priority (each step gated on being > 0):
 *  1. margin_used (API) — current margin = position_value / leverage; the
 *     authoritative, leverage-aware basis (equals initial_margin)
 *  2. isolated_margin — exact collateral committed when margin_type is ISOLATED
 *  3. entry_value / leverage — reconstruct from the leverage the user chose
 *  4. initial_margin (API) — fallback when leverage is missing
 *  5. maintenance_margin (API) — last numeric fallback
 *  6. entry_value — so we always return *something* sensible (raw return)
 *
 * Returns 0 only when every option is 0/missing (e.g. fully-closed position).
 */
export function pickMarginBasis(p: IRoiInputs): number {
  const entry = num(p.avg_entry_price);
  const amount = num(p.amount);
  const entryValue = entry * amount;

  // API-provided current margin (position_value / leverage). Authoritative and
  // leverage-aware — prefer it over any client-side reconstruction.
  const marginUsed = num(p.margin_used);
  if (marginUsed > 0) return marginUsed;

  const marginType = normalizeMarginType(
    (p.margin_type ?? null) as MarginTypeResponse | null
  );

  const isolated =
    marginType === MarginTypeResponse.Isolated ? num(p.isolated_margin) : 0;
  if (isolated > 0) return isolated;

  const leverage = num(p.leverage);
  if (leverage > 0 && entryValue > 0) return entryValue / leverage;

  const initial = num(p.initial_margin);
  if (initial > 0) return initial;

  const maintenance = num(p.maintenance_margin);
  if (maintenance > 0) return maintenance;

  return entryValue; // raw-return fallback; returns 0 if entryValue is also 0
}

/**
 * Compute ROE for a given PnL (signed dollars) against a position's margin.
 * Returns a fraction (0.47 == 47%). Returns 0 when no usable basis exists.
 */
export function calculateRoi(
  pnl: string | number | null | undefined,
  position: IRoiInputs
): number {
  const pnlNum = pnl == null ? 0 : typeof pnl === "number" ? pnl : parseFloat(String(pnl));
  if (!Number.isFinite(pnlNum)) return 0;
  const basis = pickMarginBasis(position);
  if (basis <= 0) return 0;
  return pnlNum / basis;
}

/**
 * Resolve the ROE to DISPLAY for a position, as a fraction (0.47 == 47%).
 *
 * This is the single entry point every view must use. It prefers the backend's
 * authoritative `return_on_equity` (= unrealized_pnl / margin_used) and falls
 * back to `calculateRoi` — the documented margin-basis priority chain — only
 * when the API omits it.
 *
 * Centralised deliberately: the positions table, the position-details modal and
 * the PnL share card previously each inlined their own
 * `apiNum(return_on_equity) ?? <something>`, and the details modal's "something"
 * was `calculatePnlPercent`, which divides by maintenance_margin instead of
 * margin_used. On the fallback path that produced a visibly different ROE for
 * the same position depending on which view you opened. One function, one basis.
 */
export function resolveRoi(
  pnl: string | number | null | undefined,
  position: IRoiInputs
): number {
  return apiNum(position.return_on_equity) ?? calculateRoi(pnl, position);
}

/* ──────────────── inline reasoning / expected outputs ────────────────────
 * (Verified by tests, kept here for code reviewers.)
 *
 *  ETH 1x  (entry 2121.64, mark 2113.33, amt 0.0023, lev "1")
 *    entry_value = 4.879772, basis = 4.879772/1 = 4.879772
 *    pnl = -0.0191 → roi = -0.0039 → -0.39%   (== raw price move)
 *
 *  SILVER 20x (entry 75.883, amt 0.12, lev "20", pnl 0.277)
 *    entry_value = 9.10596, basis = 9.10596/20 = 0.4553
 *    roi = 0.277/0.4553 = 0.6084 → +60.84%  (~20× raw return)
 *
 *  ETH 20x earlier  (entry 2042.52, amt 0.0008, lev "20", pnl 0.0626)
 *    entry_value = 1.6336, basis = 0.0817
 *    roi = 0.0626/0.0817 = 0.766 → +76.60%
 *
 *  Isolated 5x  (isolated_margin 100, pnl 25)
 *    basis = 100, roi = 0.25 → +25%
 *
 *  Missing leverage, has initial_margin (tier 5%)
 *    Falls through to API initial_margin; returns tier-based ROE.
 *
 *  Everything 0 / closed position
 *    pickMarginBasis returns 0 → calculateRoi returns 0.
 * ──────────────────────────────────────────────────────────────────────── */
